import { useState } from 'react'
import { Dialog, DialogPanel, Popover, PopoverButton, PopoverGroup, PopoverPanel, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Link } from "react-router-dom"

import logo from '../assets/logo-web-1.svg'
import logoWhite from '../assets/logo-white.png'

import { useScrollDirection } from '../hooks'
import { useUserContext } from '../context/userContext'

import UserProfileModal from './userProfileModal'

import {
  ArrowPathIcon,
  ArrowRightIcon,
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  NewspaperIcon,
  SquaresPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { ChevronDownIcon, PhoneIcon } from '@heroicons/react/20/solid'

const services = [
  { name: 'Website Development', description: 'Modern websites, dashboards, and scalable business platforms.', href: '/portfolio/software?filter=web', icon: ChartPieIcon },
  { name: 'Mobile App Development', description: 'Fast, user-friendly app experiences across devices.', href: '/portfolio/software?filter=mobile', icon: CursorArrowRaysIcon },
  { name: 'AI Integration', description: 'Smart assistants, automations, and AI-powered product features.', href: '/blog', icon: SquaresPlusIcon },
  { name: 'Graphics & UI/UX Design', description: 'Brand systems, interfaces, and polished visual experiences.', href: '/portfolio/design', icon: ArrowPathIcon },
]

const callsToAction = [
  { name: 'Blog & News', href: '/blog', icon: NewspaperIcon },
  { name: 'Contact us', href: '/contact-us', icon: PhoneIcon },
]

const navLinks = [
  { name: 'Blog & News', href: '/blog' },
  { name: 'About', href: '/about' },
  { name: 'Join our Team', href: '/join' },
]

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isVisible } = useScrollDirection();
  const { isAuthenticated, logout } = useUserContext();

  return (
    <header className={`fixed top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-saturate-150 transition-transform duration-300 dark:border-gray-800 dark:bg-dark-bg/95 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 px-5 py-3.5 lg:px-8">
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 rounded-lg p-1.5 transition duration-200 hover:bg-primary/5 dark:hover:bg-white/10">
            <span className="sr-only">Afroawi Technologies</span>
            <img alt="" src={logo} className="hidden h-9 w-auto dark:hidden sm:flex" />
            <img alt="" src={logoWhite} className="hidden h-9 w-auto dark:sm:flex" />
            <img alt="" src={logo} className="flex h-9 w-auto sm:hidden dark:hidden" />
            <img alt="" src={logoWhite} className="hidden h-9 w-auto sm:hidden dark:flex" />
          </Link>
        </div>

        <PopoverGroup className="hidden lg:flex lg:items-center lg:gap-x-1">
          <Popover className="relative">
            {({ close }) => (
              <>
                <PopoverButton className="flex items-center gap-x-1 rounded-lg px-3.5 py-2 text-sm font-medium leading-6 text-gray-700 duration-200 hover:bg-gray-100 hover:text-primary focus:outline-none dark:text-gray-200 dark:hover:bg-white/10 dark:hover:text-secondary">
                  Services
                  <ChevronDownIcon aria-hidden="true" className="h-4 w-4 flex-none text-gray-400" />
                </PopoverButton>

                <PopoverPanel
                  transition
                  className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl shadow-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in dark:border-gray-800 dark:bg-dark-card"
                >
                  <div className="p-4">
                    {services.map((item) => (
                      <div
                        key={item.name}
                        className="group relative flex items-center gap-x-5 rounded-lg p-3 text-sm leading-6 transition duration-200 hover:bg-gray-50 dark:hover:bg-white/5"
                      >
                        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary/10 duration-200 group-hover:bg-secondary dark:bg-white/10">
                          <item.icon aria-hidden="true" className="h-5 w-5 text-primary duration-200 group-hover:scale-110 dark:text-secondary" />
                        </div>
                        <div className="flex-auto">
                          <Link to={item.href} onClick={() => close()} className="block font-medium text-gray-900 dark:text-white">
                            {item.name}
                            <span className="absolute inset-0" />
                          </Link>
                          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100 bg-gray-50 dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-800/50">
                    {callsToAction.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => close()}
                        className="group flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold leading-6 text-gray-700 transition duration-200 hover:bg-secondary/20 hover:text-primary dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        <item.icon aria-hidden="true" className="h-4 w-4 flex-none text-gray-400 duration-200 group-hover:text-secondary" />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </PopoverPanel>
              </>
            )}
          </Popover>

          {navLinks.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="rounded-lg px-3.5 py-2 text-sm font-medium leading-6 text-gray-700 duration-200 hover:bg-gray-100 hover:text-primary dark:text-gray-200 dark:hover:bg-white/10 dark:hover:text-secondary"
            >
              {item.name}
            </Link>
          ))}
        </PopoverGroup>

        <div className="flex flex-1 items-center justify-end gap-x-3">
          {isAuthenticated ? (
            <div className="hidden items-center lg:flex">
              <UserProfileModal/>
            </div>
          ) : (
            <Link
              to="/request-meeting"
              className="hidden items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition duration-200 hover:bg-primary/90 lg:inline-flex"
            >
              Start a Project
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}

          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex items-center justify-center rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>

      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10 bg-gray-900/20" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 dark:bg-dark-bg sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center gap-x-6">
            <Link to="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
              <span className="sr-only">Afroawi Technologies</span>
              <img alt="" src={logo} className="h-8 w-auto dark:hidden" />
              <img alt="" src={logoWhite} className="hidden h-8 w-auto dark:flex" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="ml-auto rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-100 dark:divide-gray-800">
              <div className="space-y-1 py-6">
                <Disclosure as="div">
                  <DisclosureButton className="group flex w-full items-center justify-between rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5">
                    Services
                    <ChevronDownIcon aria-hidden="true" className="h-5 w-5 flex-none group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-1 space-y-1">
                    {services.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as={Link}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="group flex flex-row items-center gap-3 rounded-lg py-2 pl-6 pr-3 text-sm font-normal leading-7 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                      >
                        <item.icon aria-hidden="true" className="h-5 w-5 text-gray-400 group-hover:text-secondary-dark dark:group-hover:text-secondary-light" />
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>

                {navLinks.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5"
                  >
                    {item.name}
                  </Link>
                ))}
                <Link
                  to="/contact-us"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5"
                >
                  Contact Us
                </Link>
                <Link
                  to="/request-meeting"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-base font-semibold text-white transition duration-200 hover:bg-primary/90"
                >
                  Start a Project
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
              {isAuthenticated && (
                <div className="py-6">
                  <button
                    onClick={logout}
                    className="block w-full rounded-lg px-3 py-2.5 text-left text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
